import React, { useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import { Button, ButtonProps, Flex, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { ApiPersonDetailReviewsListPetType as PetTypesReview } from '@rover/rsdk/src/apiClient/latest/model/apiPersonDetailReviewsListPetType';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import type { RoverTheme } from '@rover/types/src/RoverTheme';

import { PetTypeReviewsFilterChanged } from '../../analyticsEvents';

type Props = {
  onChange: (serviceTypeCategory: PetTypesReview) => void;
  initialPetType?: PetTypesReview;
};

const getCommonButtonStyle = (theme: RoverTheme): ButtonProps['sx'] => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: theme.radii.secondary,
  maxHeight: '34px',
  paddingY: `${theme.space['2x']} !important`,
  paddingX: `${theme.space['3x']} !important`,
  '&:hover': {
    backgroundColor: theme.background.tertiary,
  },
  '&:focus': {
    backgroundColor: theme.background.accent_secondary,
  },
  width: ['100%', 'auto'],
  margin: '2px',
});

const getSelectedButtonStyle = (theme: RoverTheme): ButtonProps['sx'] => ({
  ...getCommonButtonStyle(theme),
  boxShadow: `0px 0px 0px 2px ${theme.colors.interactive_text.primary_active}`,
  backgroundColor: theme.background.accent_secondary,
});

const PetTypeReviewFilter = ({
  onChange,
  initialPetType: initialServiceTypeCategory,
}: Props): JSX.Element | null => {
  const { t } = useLingui();
  const theme = useTheme();
  const [selectedCategory, setSelectedCategory] = useState<PetTypesReview | undefined>(
    initialServiceTypeCategory
  );

  if (initialServiceTypeCategory === undefined) {
    return null;
  }

  const mapTypeToText = {
    [PetTypesReview.dog]: 'Dog only',
    [PetTypesReview.cat]: t`Cat only`,
    [PetTypesReview.puppy]: 'Puppy only',
    all: t`All reviews`,
  };

  const filterButtons = [initialServiceTypeCategory, 'all'].map((key, idx) => {
    const petType = PetTypesReview[key];
    const isSelected = selectedCategory === petType;
    const style = isSelected ? getSelectedButtonStyle(theme) : getCommonButtonStyle(theme);
    const text = mapTypeToText[key];
    const onClick = (): void => {
      setSelectedCategory(petType);
      onChange(petType);
      emitAnalyticsEvent(new PetTypeReviewsFilterChanged({ petType: petType || 'all' }));
    };

    return (
      <Button key={`${text}-${idx}`} sx={style} onClick={onClick}>
        <Text size="100" fontWeight="semibold" sx={{ whiteSpace: 'nowrap' }}>
          {text}
        </Text>
      </Button>
    );
  });

  return <Flex gap="2x">{filterButtons}</Flex>;
};

export default PetTypeReviewFilter;
